import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
  useModal,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useCamera } from '../../hooks/useCamera';
import { useStyles } from '../../hooks/useStyles';
import {
  loadingUsersState,
  searchUsersState,
  usersIdsState,
  usersPageState,
  usersState,
} from '../../storage/userStates';
import { isValidPosition } from '../../utils/map';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import StyledTooltip from '../ui/StyledTooltip';
import UserCard from './UserCard';
import { UserModal } from './UserModal';

export function UserList() {
  const { theme, classes, css } = useStyles();
  const userIds = useRecoilValue(usersIdsState);
  const loadingUsers = useRecoilValue(loadingUsersState);
  const { search, setSearch } = useDebouncedSearch(useRecoilState(searchUsersState));
  const paginatedUsers = useRecoilValue(usersState);
  const [usersPage, setUsersPage] = useRecoilState(usersPageState);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const { openModal, closeModal, showModal } = useModal();
  const { fitToLngLats } = useCamera();
  const [initialCenter, setInitialCenter] = useState(false);

  useEffect(() => {
    if (!paginatedUsers || initialCenter) return;
    const usersPosition = paginatedUsers?.items
      .filter((user) => isValidPosition(user.lastPosition?.latitude, user.lastPosition?.longitude))
      .map((user) => [user.lastPosition?.longitude, user.lastPosition?.latitude]) as number[][];
    fitToLngLats(usersPosition);
    setInitialCenter(true);
  }, [paginatedUsers]);

  const onUpdate = () => {
    setUsersPage(0);
  };

  useEffect(() => {
    if (!scrollableRef.current) return;
    if (usersPage === 0) {
      const scrollableDiv = scrollableRef.current;
      scrollableDiv.scrollTo(0, 0);
    }
  }, [usersPage, scrollableRef]);

  const onSubmitFinished = () => {
    closeModal();
    onUpdate();
  };

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale500}
    >
      {!userIds && !loadingUsers && <EmptyState title="No cuentas con ningún usuario." />}
      {!userIds && loadingUsers && <Loading />}
      {userIds && (
        <>
          <FlexRow gap={theme.sizing.scale300}>
            <div className={classes.flexible(1)}>
              <StyledSearchBar
                name="user-search"
                value={search}
                onChange={setSearch}
                clearable
                noMargin
              />
            </div>
            <FlexRow
              gap={0}
              classNames={css({ alignItems: 'center' })}
            >
              <StyledTooltip
                content={'Nuevo'}
                showArrow={false}
              >
                <Button
                  size="mini"
                  shape="square"
                  onClick={openModal}
                  kind="tertiary"
                >
                  <MaterialIcon name={'add'} />
                </Button>
              </StyledTooltip>
              <StyledTooltip
                content={'Actualizar'}
                showArrow={false}
              >
                <Button
                  size="mini"
                  shape="square"
                  onClick={onUpdate}
                  kind="tertiary"
                >
                  <MaterialIcon name={'sync'} />
                </Button>
              </StyledTooltip>
            </FlexRow>
          </FlexRow>
          {!!paginatedUsers?.items.length && (
            <div
              id="scrollableDiv"
              className={css({ height: '100%', overflow: 'auto', marginBottom: '12px' })}
              ref={scrollableRef}
            >
              <InfiniteScroll
                dataLength={paginatedUsers?.items.length}
                next={() => setUsersPage(usersPage + 1)}
                hasMore={paginatedUsers?.hasNext ?? false}
                loader={
                  <div className={css({ height: '80px', overflow: 'hidden' })}>
                    <Loading />
                  </div>
                }
                endMessage={
                  paginatedUsers?.hasNext && (
                    <p style={{ textAlign: 'center' }}>
                      <b>Cargando...</b>
                    </p>
                  )
                }
                scrollableTarget="scrollableDiv"
              >
                <FlexColumn gap={'8px'}>
                  {userIds.map((userId) => (
                    <UserCard
                      id={userId}
                      key={userId}
                    />
                  ))}
                </FlexColumn>
              </InfiniteScroll>
            </div>
          )}
          {!userIds.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
          {/* <UsersInfo /> */}
          <UserModal
            isOpen={showModal}
            onClose={closeModal}
            onSubmitFinished={onSubmitFinished}
          />
        </>
      )}
    </FloatingCardContent>
  );
}
